import ContentLoader from 'react-content-loader';
import { Link } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import { BUTTON_SIZE } from '../../../components/generic/Button/button-constants';
import NavigationButton from '../../../components/generic/Button/NavigationButton';
import { icons } from '../../../components/generic/Icon/icons';
import { Icon } from '../../../components/generic/Icon/index';
import { LoadingBoundary } from '../../../components/generic/LoadingBoundary';
import Text from '../../../components/generic/Text';
import COLORS from '../../../theme/colors';
import { organizationPortalRoute, releaseReportRoute } from '../../helpers/routes';
import useSlug from '../../hooks/useSlug';
import type { ReleaseGroupReleasesResponse } from '../../../../shared/publicPortal/types';
import type { LoadingBoundaryProps } from '../../../components/generic/LoadingBoundary';
import type { AsyncValue } from '../../../redux/asyncValue';

import styles from './ReleaseGroup.module.less';

const HeaderLoader = () => (
  <ContentLoader
    speed={2}
    width={200}
    height={59}
    foregroundColor={COLORS.fossaGray05}
    backgroundColor={COLORS.fossaGray10}
    className={styles.headerLoader}
  >
    <rect x="0" y="20" rx="3" ry="3" width="200" height="29" />
  </ContentLoader>
);

const HeaderContent: LoadingBoundaryProps<ReleaseGroupReleasesResponse>['renderValue'] = ({ value }) => (
  <Text.H3>{ value.releaseGroup.title }</Text.H3>
);

type HeaderProps = {
  releases: AsyncValue<ReleaseGroupReleasesResponse>;
};

const Header: VoidFunctionComponent<HeaderProps> = ({ releases }) => {
  const slug = useSlug();
  const isPublic = Boolean(releases.value?.releaseGroup.publicOnPortal);
  // Provide a good user experience by redirecting to the correct portal tab (private/public) based on the release group's visibility
  const allSBOMsLink = organizationPortalRoute(slug, { isPublic });
  const reportUrl = releases.isLoading || !releases.value?.releases[0] ? '' : releaseReportRoute(slug, releases.value.releases[0].id);

  return (
    <div className={styles.header}>
      <div className="block">
        <Link to={allSBOMsLink}><Icon icon={icons.arrowLeft} /> All SBOMs</Link>
        <LoadingBoundary
          asyncValue={{ ...releases, isLoading: false }}
          renderValue={HeaderContent}
          renderLoading={<HeaderLoader />}
          renderEmpty={<HeaderLoader />}
          renderError={HeaderLoader}
          />
      </div>
      <NavigationButton
        to={reportUrl}
        size={BUTTON_SIZE.LARGE}
        icon={icons.download}
        isDisabled={releases.isLoading || !releases.value?.releases[0]}
        openInNewTab
      >
        Download SBOM
      </NavigationButton>
    </div>
  );
};

export default Header;
